import axios from "axios";

// Client talks to our proxy only; no API key on the client.
// Deployed, the app and the API are the same Worker, so the base is empty and
// every call is same-origin — no CORS, no second host to keep in step. In dev
// the API is a separate Express process on :5000.
// `||` rather than `??` on purpose: an empty REACT_APP_API_BASE means
// same-origin, and ?? would have treated "" as a value to keep. Belt and
// braces with .env.production, which is what stops .env's localhost value
// reaching a deployed build.
const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (process.env.NODE_ENV === "production" ? "" : "http://localhost:5000");
const PPT_API_URL = `${API_BASE}/api/cards`;
const PPT_SETS_URL = `${API_BASE}/api/sets`;

export async function fetchCards(params = {}) {
  const res = await axios.get(PPT_API_URL, { params });
  return res.data;
}

/**
 * Many cards by id, in one request.
 *
 * What the stack prices with. It used to make one search per card, which for
 * a stack of sixty was sixty requests inside a minute — the Worker's own
 * per-IP ceiling — so a stack worth pricing got 429s partway through. This is
 * one request whatever the size, answered from the per-id cache where it can
 * be.
 *
 * @returns { cards: { [id]: card | null }, unavailable: id[] }
 *          null for an id the source has no listing for; `unavailable` names
 *          the ids the source could not be asked about at all, which is a
 *          different fact and gets different words on screen
 */
export async function fetchCardsByIds(ids) {
  const list = [...new Set((ids || []).filter(Boolean))];
  if (!list.length) return { cards: {}, unavailable: [] };
  const res = await axios.get(`${API_BASE}/api/cards/batch`, {
    params: { ids: list.join(",") },
  });
  return {
    cards: res.data?.data || {},
    unavailable: Array.isArray(res.data?.unavailable) ? res.data.unavailable : [],
  };
}

/* Names and pictures, no prices — what the stack's card picker runs on. A
   priced search costs a detail call per card, which is what caps it at ten
   results; this one returns every match, so "umbreon" shows you all of them
   and the price is fetched for whichever you pick. */
export async function findCards(name, params = {}) {
  const res = await axios.get(`${API_BASE}/api/find`, {
    params: { search: name, ...params },
  });
  const cards = res.data?.data || [];
  /* When a search finds nothing, the server tries again with the nearest real
     card name — which is how a misread "Grenihjae" still lands on Greninja.
     Carried on the array so nothing that already uses this has to change. */
  cards.correctedTo = res.data?.metadata?.corrected || null;
  /* And when it finds nothing by name, it reads part of the query as a set —
     "Umbreon prismatic" is Umbreon in Prismatic Evolutions. Worth saying which
     set, since the query never named it in full. */
  cards.narrowedTo = res.data?.metadata?.narrowedTo || null;
  return cards;
}

export async function fetchSets(params = {}) {
  // Lightweight client-side cache for sets list
  const cachePrefix = "ppt_sets_cache_v1";
  const ttlMs = 12 * 60 * 60 * 1000; // 12 hours
  const stableKey = (() => {
    const keys = Object.keys(params || {}).sort();
    const stable = {};
    for (const k of keys) stable[k] = params[k];
    return `${cachePrefix}:${JSON.stringify(stable)}`;
  })();
  const now = Date.now();

  try {
    if (typeof window !== "undefined" && window.localStorage) {
      const raw = window.localStorage.getItem(stableKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.expiresAt && parsed.expiresAt > now && parsed.payload) {
          return parsed.payload;
        }
      }
    }
  } catch {}

  const res = await axios.get(PPT_SETS_URL, { params });
  const payload = res.data;

  try {
    if (typeof window !== "undefined" && window.localStorage) {
      const record = { payload, expiresAt: now + ttlMs };
      window.localStorage.setItem(stableKey, JSON.stringify(record));
    }
  } catch {}

  return payload;
}

// Market prices for One Piece cards, keyed by the codes you ask for. A code
// whose parallel doesn't exist upstream comes back null rather than guessing.
export async function fetchOnePiecePrices(codes) {
  const list = (codes || []).filter(Boolean);
  if (!list.length) return {};
  const res = await axios.get(`${API_BASE}/api/op/prices`, {
    params: { codes: list.join(",") },
  });
  return res.data?.prices || {};
}

// Fetch a single card with eBay graded sales data (PSA/CGC/BGS)
export async function fetchCardEbayData(tcgPlayerId) {
  const res = await axios.get(`${PPT_API_URL}/ebay/${encodeURIComponent(tcgPlayerId)}`);
  return res.data;
}
